'use client';
import { ReactNode, useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { SharedAppShell, type ShellConfig, type NavItem } from '@tb/ui/AppShell';

const NAV: NavItem[] = [
 {
 label: 'Главная',
 icon: 'grid',
 children: [{ href: '/companies/{id}', label: 'Обзор', match: '^/companies/[^/]+$', icon: 'grid' }],
 },
 {
 label: 'Команды',
 icon: 'users',
 children: [
 { href: '/companies/{id}/teams', label: 'Все команды', match: '^/companies/[^/]+/teams$', icon: 'users' },
 { href: '/companies/{id}/members', label: 'Сотрудники', match: '^/companies/[^/]+/members', icon: 'users' },
 ],
 },
 {
 label: 'Типологии',
 icon: 'dna',
 children: [
 { href: '/psytypes', label: 'Типы личности (32)', match: '^/psytypes|^/archetypes', icon: 'brain' },
 { href: '/team-archetypes', label: 'Архетипы команд (6)', match: '^/team-archetypes', icon: 'users' },
 { href: '/articles', label: 'Статьи', match: '^/articles', icon: 'book' },
 ],
 },
 {
 label: 'Найм',
 icon: 'briefcase',
 children: [
 { href: '/companies/{id}/hiring', label: 'Вакансии', match: '^/companies/[^/]+/hiring', icon: 'briefcase' },
 ],
 },
 {
 label: 'Настройки',
 icon: 'gear',
 children: [
 { href: '/settings', label: 'Аккаунт', match: '^/settings', icon: 'gear' },
 { href: '/companies/{id}/settings', label: 'Компания', match: '^/companies/[^/]+/settings', icon: 'building' },
 ],
 },
];

function CompanySwitcher() {
 const router = useRouter();
 const [companies, setCompanies] = useState<any[]>([]);
 const [open, setOpen] = useState(false);
 const [current, setCurrent] = useState<any>(null);

 useEffect(() => {
 api.myCompanies().then((r: any) => {
 const list = r?.data?.companies ?? [];
 setCompanies(list);
 const found = list[0];
 if (found) setCurrent(found);
 }).catch(() => setCompanies([]));
 }, []);

 useEffect(() => {
 if (!open) return;
 const close = () => setOpen(false);
 setTimeout(() => document.addEventListener('click', close), 0);
 return () => document.removeEventListener('click', close);
 }, [open]);

 if (companies.length === 0) return null;

 return (
 <div className="relative">
 <button
 onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
 className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-semibold text-foreground hover:bg-muted/50"
 >
 <span className="flex h-5 w-5 items-center justify-center rounded bg-primary text-[10px] font-bold text-primary-foreground">
 {(current?.company?.name || '?')[0].toUpperCase()}
 </span>
 <span className="max-w-[140px] truncate">{current?.company?.name ?? 'Выбрать компанию'}</span>
 <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
 </button>
 {open && (
 <div className="absolute left-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-xl border border-border bg-popover shadow-lg">
 {companies.map((c: any) => (
 <button key={c.company.id}
 onClick={(e) => { e.stopPropagation(); setOpen(false); setCurrent(c); router.push(`/companies/${c.company.id}`); }}
 className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 ${
 current?.company?.id === c.company.id ? 'bg-accent text-accent-foreground font-semibold' : 'text-foreground'
 }`}>
 <span className="flex h-5 w-5 items-center justify-center rounded bg-muted text-[10px] font-bold text-muted-foreground">
 {c.company.name[0].toUpperCase()}
 </span>
 <span className="flex-1 truncate">{c.company.name}</span>
 <span className="text-[10px] text-muted-foreground">{c.role}</span>
 </button>
 ))}
 <div className="border-t border-border">
 <button onClick={() => { setOpen(false); router.push('/'); }}
 className="flex w-full items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-accent/30">
 <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
 Создать компанию
 </button>
 </div>
 </div>
 )}
 </div>
 );
}

export default function AppShell({ children }: { children: ReactNode }) {
 const router = useRouter();
 const pathname = usePathname();
 const { user, loading, logout } = useAuth();

 // Public routes — don't gate with auth
 const isPublic =
 pathname === '/login' ||
 pathname === '/register' ||
 pathname === '/forgot-password' ||
 pathname === '/reset-password';

 // Redirect to /login when auth state resolves and there's no user (skip public routes)
 useEffect(() => {
 if (!isPublic && !loading && !user) {
 router.replace('/login');
 }
 }, [isPublic, loading, user, router]);

 // On public routes, render children directly without any gate
 if (isPublic) return <>{children}</>;

 // On protected routes, show loader until auth resolves
 if (loading || !user) {
 return <div className="flex min-h-screen items-center justify-center bg-background">
 <div className="text-sm text-muted-foreground">Загрузка…</div>
 </div>;
 }

 const config: ShellConfig = {
 brandLabel: 'B2B',
 nav: NAV,
 userEmail: user.email,
 onLogout: () => { logout(); router.replace('/login'); },
 headerExtra: <CompanySwitcher />,
 };

 return <SharedAppShell config={config}>{children}</SharedAppShell>;
}
